import './style.scss'
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { Select, Button, Tooltip } from 'antd';
import {
    StepBackwardOutlined,
    StepForwardOutlined,
    PauseOutlined,
    CaretRightOutlined,
    CalendarOutlined
} from '@ant-design/icons'

import utc from 'dayjs/plugin/utc'; // Import UTC plugin for dayjs
import timezone from 'dayjs/plugin/timezone'; // Import timezone plugin for dayjs
dayjs.extend(utc); // Enable the UTC plugin
dayjs.extend(timezone); // Enable the timezone plugin
import 'dayjs/locale/en'; // Import the locale you want to use

import { useVideoContext } from "./VideoContext"; // Import the context hook
import SeekBar2 from './SeekBar2';
import DatePickerModal from './Components';


function MasterControls({time_zone, unix_timestamp_s }) {

    const {
        playAll,
        pauseAll,
        playingRef,
        stepBackwardsAll,
        stepForwardAll,
        requestSeek,
        masterIsLive,
        setScrollWheelStart,
        masterSpeed,
        setSpeedAll
    } = useVideoContext();

    const usTimeZoneMap = {
        EST: "America/New_York",      // Eastern Standard Time
        EDT: "America/New_York",      // Eastern Daylight Time
        CST: "America/Chicago",       // Central Standard Time
        CDT: "America/Chicago",       // Central Daylight Time
        MST: "America/Denver",        // Mountain Standard Time
        MDT: "America/Denver",        // Mountain Daylight Time
        PST: "America/Los_Angeles",   // Pacific Standard Time
        PDT: "America/Los_Angeles",   // Pacific Daylight Time
        AKST: "America/Anchorage",    // Alaska Standard Time
        AKDT: "America/Anchorage",    // Alaska Daylight Time
        HST: "Pacific/Honolulu"       // Hawaii Standard Time (no DST)
    };

    const handleDateSelect = (value) => {
        const tz = usTimeZoneMap[time_zone] || "UTC";

        // Take the raw fields picked and reinterpret them in the trailer's timezone
        const picked = value.format("YYYY-MM-DD HH:mm:ss");
        const localTime = dayjs.tz(picked, "YYYY-MM-DD HH:mm:ss", tz);
        // Convert to UTC ms for backend
        const targetMs = localTime.valueOf();

        requestSeek(targetMs);
        setScrollWheelStart({ time: targetMs, nonce: Date.now() });
        setShowCalendar(false);
    };

    const [showCalendar, setShowCalendar] = useState(false)

    const disableDate = current => {
        const today = dayjs().endOf('day');
        const twentyDaysAgo = dayjs().subtract(20, 'day').startOf('day');

        // Disable if before 20 days ago
        if (current < twentyDaysAgo) return true;

        // Disable if after today
        if (current > today) return true;

        // Otherwise allowed
        return false;
    };

    const disabledTime = (current) => {
    const now = dayjs();
    
    // Only restrict time if it's today
    if (!current || !current.isSame(now, 'day')) {
        return {}; // No time restrictions for other days
    }

    // For today, disable future times
    const currentHour = now.hour();
    const currentMinute = now.minute();
    const currentSecond = now.second();

    return {
        disabledHours: () => {
        // Disable hours after current hour
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h > currentHour);
        },
        disabledMinutes: (selectedHour) => {
        // If selected hour is current hour, disable future minutes
        if (selectedHour === currentHour) {
            return Array.from({ length: 60 }, (_, i) => i).filter(m => m > currentMinute);
        }
        return [];
        },
        disabledSeconds: (selectedHour, selectedMinute) => {
        // If selected hour and minute are current, disable future seconds
        if (selectedHour === currentHour && selectedMinute === currentMinute) {
            return Array.from({ length: 60 }, (_, i) => i).filter(s => s > currentSecond);
        }
        return [];
        }
    };
    };

    function useIsMobile(breakpoint = 768) {
        const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);

        useEffect(() => {
            const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }, [breakpoint]);

        return isMobile;
    }

    const renderLeftControls = () => {
        return (
            <div className='left-controls vertical-center row'>
                <Tooltip
                    title='Speed Selection'
                >
                <Select 
                    value={masterSpeed.current}
                    defaultValue="1x" 
                    style={{ width: 'auto' }}
                    onChange={(value) => {
                        setSpeedAll(value);
                    }}
                    options={[
                        {value: '1', label: <span>1x</span>},
                        { value: '2', label: <span>2x</span> },
                        { value: '4', label: <span>4x</span> },
                        { value: '8', label: <span>8x</span> }
                        ]} 
                    disabled={masterIsLive}
                />
                </Tooltip>

                <Tooltip
                    title='Back Step'
                >
                    <Button
                        onClick={stepBackwardsAll}
                        ghost
                        className='master-button-left'
                    >
                        <StepBackwardOutlined
                            className='playback-icons'
                        />
                    </Button>
                </Tooltip>
                <Tooltip
                    title='Play/Pause'
                >
                    {(playingRef.current) ?
                        <Button
                            onClick={pauseAll}
                            ghost
                            className='master-button-left'
                        >
                            <PauseOutlined
                                className='playback-icons'
                            />
                        </Button>
                        :
                        <Button
                            onClick={playAll}
                            ghost
                            className='master-button-left'
                        >
                            <CaretRightOutlined
                                className='playback-icons'

                            />
                        </Button>
                    }
                </Tooltip>
                <Tooltip
                    title='Forward Step'
                >
                    <Button
                        onClick={stepForwardAll}
                        ghost
                        className='master-button-left'
                        disabled={masterIsLive}
                    >
                        <StepForwardOutlined
                            className='playback-icons'
                        />
                    </Button>
                </Tooltip>
            </div>
        )
    }

    const renderRightControls = () => {
        return (
            
        <div>
            <div
                style={{
                    height: 'auto',
                    position: 'relative'
                }}
            >
                <Button
                    onClick={() => setShowCalendar(!showCalendar)}
                >
                    <CalendarOutlined />
                </Button>
                
                <DatePickerModal
                    showCalendar={showCalendar}
                    onClose={() => setShowCalendar(false)}
                    onDateSelect={handleDateSelect}
                    disableDate={disableDate}
                    disableTime={disabledTime}
                    timeZone={usTimeZoneMap[time_zone] || "UTC"}
                />
                
            </div>
        </div>
        )
    }


    const isMobile = useIsMobile();
    return (
        <div className='playback-controls-container column horizontal-center vertical-center'>
            { isMobile ? (
                 <>
                <div className="playback-seekbar-container horizontal-center vertical-center">
                    <SeekBar2 
                    time_zone = {time_zone}
                    unix_timestamp_s = {unix_timestamp_s}
                    />
                </div>
                <div className="row mobile-controls vertical-center horizontal-center" style={{ width: "100%", marginBottom: "1%" }}>
                    { renderLeftControls() }
                    { renderRightControls() }
                </div>
        </>
            ) :
            <div className='row vertical-center horizontal-center' style={{ width: '100%', maxWidth: '100%' }}>
                { renderLeftControls() }
                <div className='playback-seekbar-container horizontal-center vertical-center'>
                    <SeekBar2 
                    time_zone = {time_zone}
                    unix_timestamp_s = {unix_timestamp_s}
                    />
                </div>
                { renderRightControls()}
            </div>
        }
        </div>
        
    )
}

export default MasterControls